/* eslint-disable react/prop-types */
import { Descriptions } from 'antd';
import { isEmpty } from 'lodash/fp';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouterSpecsFragment } from '../../../../../api/fragments/RouterSpecs';
import { type WebPageEndpointSpecsFragment } from '../../../../../api/fragments/WebPageEndpointSpecs';
import {
    CreateWebPageEndpointDocument,
    type CreateWebPageEndpointMutation,
    type CreateWebPageEndpointMutationVariables,
} from '../../../../../api/mutations/createWebPageEndpoint';
import {
    UpdateWebPageEndpointDocument,
    type UpdateWebPageEndpointMutation,
    type UpdateWebPageEndpointMutationVariables,
} from '../../../../../api/mutations/updateWebPageEndpoint';
import { useGetWebPageOptionsQuery } from '../../../../../api/queries/getWebPageOptions';
import { type WebPageEndpointSettings } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import SelectField from '../../../../../components/fields/SelectField';
import { type EndpointImplementation } from './types';

type FormProps = {
    moduleId?: string;
    endpoint?: RouterSpecsFragment['endpoints'][number];
};
const FormComponent = ({ moduleId: moduleIdFromProps, endpoint }: FormProps) => {
    const { t } = useTranslation(['routerDetails']);

    const moduleId = endpoint?.__typename === 'WebPageEndpoint' ? endpoint.websiteModule.id : moduleIdFromProps;

    const { data } = useGetWebPageOptionsQuery({ variables: { moduleId }, skip: isEmpty(moduleId) });
    const options = useMemo(
        () => data?.options.map(({ id, pageTitle }) => ({ value: id, label: pageTitle.defaultValue })) ?? [],
        [data]
    );

    return (
        <>
            <InputField {...t('routerDetails:endpoints.fields.pathname', { returnObjects: true })} name="pathname" />
            <InputField {...t('routerDetails:endpoints.fields.title', { returnObjects: true })} name="title" />
            <SelectField
                {...t('routerDetails:endpoints.fields.webPageId', { returnObjects: true })}
                name="webPageId"
                options={options}
                showSearch
            />
        </>
    );
};

const withModuleId = (FormComponent: React.FC<{ moduleId: string }>, moduleId: string) =>
    function FormComponentWithModuleId() {
        return <FormComponent moduleId={moduleId} />;
    };

const implementation: EndpointImplementation<
    WebPageEndpointSpecsFragment,
    WebPageEndpointSettings & { websiteModuleId?: string; webPageId?: string },
    { moduleId: string }
> = {
    getTitle: t => t('routerDetails:endpoints.WebPageEndpoint.title'),
    ViewPanelComponent: ({ endpoint }) => (
        <Descriptions size="small" bordered>
            <Descriptions.Item label="Module">{endpoint.websiteModule.displayName}</Descriptions.Item>
        </Descriptions>
    ),
    getIsPrivate: () => false,
    FormComponent,
    getFormComponent: ({ moduleId }) => withModuleId(FormComponent, moduleId),
    getFormInitialValues: endpoint => ({
        pathname: endpoint.pathname,
        title: endpoint.title,
        webPageId: endpoint.webPageId,
    }),
    submitCreation: (apolloClient, routerId, { websiteModuleId, ...others }) => {
        if (!websiteModuleId) {
            throw new Error('websiteModuleId is required');
        }

        return apolloClient.mutate<CreateWebPageEndpointMutation, CreateWebPageEndpointMutationVariables>({
            mutation: CreateWebPageEndpointDocument,
            variables: { routerId, websiteModuleId, settings: others },
        });
    },
    submitUpdate: (apolloClient, routerId, endpointId, values) =>
        apolloClient.mutate<UpdateWebPageEndpointMutation, UpdateWebPageEndpointMutationVariables>({
            mutation: UpdateWebPageEndpointDocument,
            variables: { routerId, endpointId, settings: values },
        }),
};

export default implementation;
